/**
 * API client for backend communication
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiClient {
  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    
    // Get token from localStorage
    const token = localStorage.getItem('access_token');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        // Handle 401 - redirect to login
        if (response.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Request failed with status ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth
  async login(email, password) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  // Import - uses FormData file upload to prevent browser freezing
  async importCSVFile(file, importType = 'auto') {
    const url = `${API_URL}/api/import/upload-csv`;
    const token = localStorage.getItem('access_token');
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_type', importType);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `Upload failed with status ${response.status}`);
    }
    
    return await response.json();
  }

  // Legacy JSON import (kept for backward compatibility)
  async importInflowCSV(csvContent, importType = 'auto') {
    return this.request('/api/import/inflow-csv', {
      method: 'POST',
      body: JSON.stringify({ file_content: csvContent, import_type: importType }),
    });
  }

  async clearImportedData(collectionType) {
    return this.request(`/api/import/clear/${collectionType}`, { method: 'DELETE' });
  }

  // Products
  async getProducts(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/products${query ? '?' + query : ''}`);
  }

  async getProduct(id) {
    return this.request(`/api/products/${id}`);
  }

  async createProduct(data) {
    return this.request('/api/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProduct(id, data) {
    return this.request(`/api/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProduct(id) {
    return this.request(`/api/products/${id}`, { method: 'DELETE' });
  }

  // Variants
  async getVariants(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/variants${query ? '?' + query : ''}`);
  }

  async getVariant(id) {
    return this.request(`/api/variants/${id}`);
  }

  async createVariant(data) {
    return this.request('/api/variants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVariant(id, data) {
    return this.request(`/api/variants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVariant(id) {
    return this.request(`/api/variants/${id}`, { method: 'DELETE' });
  }

  // Barcode lookup
  async lookupBarcode(barcode) {
    return this.request(`/api/barcode/${encodeURIComponent(barcode)}`);
  }

  async searchProducts(query) {
    // limit=500 effectively unlimited for any realistic search (your largest
    // single-word matches are ~130). Catalog is ~1.7k items total, so even a
    // worst-case broad term still fits comfortably.
    return this.request(`/api/products/search?q=${encodeURIComponent(query)}&limit=500`);
  }


  // Inventory
  async getInventory(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/inventory${query ? '?' + query : ''}`);
  }

  async updateInventory(variantId, data) {
    return this.request(`/api/inventory/${variantId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Vendors

  // Notifications
  async getNotifications(unreadOnly = true) {
    return this.request(`/api/notifications?unread_only=${unreadOnly}`);
  }
  async getNotificationCount() {
    return this.request('/api/notifications/count');
  }
  async markNotificationRead(id) {
    return this.request(`/api/notifications/${id}/read`, { method: 'PUT' });
  }
  async markAllNotificationsRead() {
    return this.request('/api/notifications/read-all', { method: 'PUT' });
  }

  // Vendors
  async getVendors(params = {}) {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''));
    const query = new URLSearchParams(clean).toString();
    return this.request(`/api/vendors${query ? '?' + query : ''}`);
  }

  async getVendor(id) {
    return this.request(`/api/vendors/${id}`);
  }

  async createVendor(data) {
    return this.request('/api/vendors', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVendor(id, data) {
    return this.request(`/api/vendors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVendor(id) {
    return this.request(`/api/vendors/${id}`, {
      method: 'DELETE',
    });
  }

  // Customers
  async getCustomers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/customers${query ? '?' + query : ''}`);
  }

  async searchCustomers(query = '') {
    return this.request(`/api/customers/search?q=${encodeURIComponent(query)}`);
  }

  async getCustomer(id) {
    return this.request(`/api/customers/${id}`);
  }

  async createCustomer(data) {
    return this.request('/api/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomer(id, data) {
    return this.request(`/api/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCustomer(id) {
    return this.request(`/api/customers/${id}`, { method: 'DELETE' });
  }

  async getCustomerSalesOrders(id) {
    return this.request(`/api/customers/${id}/sales-orders`);
  }

  // Sales Orders
  async getSalesOrders(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/sales-orders${query ? '?' + query : ''}`);
  }

  async getSalesOrder(id) {
    return this.request(`/api/sales-orders/${id}`);
  }

  async createSalesOrder(data) {
    return this.request('/api/sales-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getLeaseNames(query = '') {
    return this.request(`/api/sales-orders/lease-names?q=${encodeURIComponent(query)}`);
  }

  async completeSalesOrder(id) {
    return this.request(`/api/sales-orders/${id}/complete`, { method: 'POST' });
  }

  async updateSalesOrderPayment(id, data) {
    return this.request(`/api/sales-orders/${id}/payment`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Purchase Orders
  async getPurchaseOrders(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/purchase-orders${query ? '?' + query : ''}`);
  }

  async getPurchaseOrder(id) {
    return this.request(`/api/purchase-orders/${id}`);
  }

  async createPurchaseOrder(data) {
    return this.request('/api/purchase-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePurchaseOrder(id, data) {
    return this.request(`/api/purchase-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async receivePurchaseOrder(id, data) {
    return this.request(`/api/purchase-orders/${id}/receive`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Payments
  async createPayment(data) {
    return this.request('/api/payments/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Dashboard
  async getDashboardStats() {
    return this.request('/api/dashboard/stats');
  }

  async getAnalytics() {
    return this.request('/api/dashboard/analytics');
  }

  // Barcode Generator
  async generateBarcode(sku, barcodeType = 'code128') {
    return this.request(`/api/barcode/generate/${encodeURIComponent(sku)}?barcode_type=${barcodeType}`);
  }

  // Price Schemes
  async getPriceSchemes() {
    return this.request('/api/price-schemes');
  }

  async updatePriceScheme(schemeName, data) {
    const params = new URLSearchParams(data).toString();
    return this.request(`/api/price-schemes/${schemeName}?${params}`, { method: 'PUT' });
  }

  async getProductPrices(productId) {
    return this.request(`/api/products/${productId}/prices`);
  }

  // Toggle product taxable status
  async updateProductTaxable(productId, isTaxable) {
    return this.request(`/api/products/${productId}/taxable`, {
      method: 'PATCH',
      body: JSON.stringify({ is_taxable: isTaxable }),
    });
  }

  // Add new inventory item (product + variant + inventory)
  async addInventoryItem(data) {
    return this.request('/api/inventory/add-item', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Square
  async getSquareConfig() {
    return this.request('/api/square/config');
  }

  async processSquarePayment(data) {
    return this.request('/api/pos/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Product reorder
  async reorderProduct(productId) {
    return this.request(`/api/products/${productId}/reorder`, { method: 'POST' });
  }

  // Product inventory update
  async updateProductInventory(productId, data) {
    return this.request(`/api/products/${productId}/inventory`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Sales tax report
  async getSalesTaxReport(fromDate, toDate, save = true) {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (!save) params.append('save', 'false');
    return this.request(`/api/reports/sales-tax?${params.toString()}`);
  }

  // Bulk barcode generation
  async generateBulkBarcodes(productIds) {
    return this.request('/api/barcode/bulk-generate', {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds }),
    });
  }

  // Admin: Login History & Sessions
  async getLoginHistory(limit = 100) {
    return this.request(`/api/admin/login-history?limit=${limit}`);
  }

  async getActiveSessions() {
    return this.request('/api/admin/active-sessions');
  }

  async getSessionStats() {
    return this.request('/api/admin/session-stats');
  }

  async logout() {
    return this.request('/api/auth/logout', { method: 'POST' });
  }

  // Price Management (unified for all price types)
  async updatePrice(productId, priceType, newPrice, effectiveDate) {
    return this.request(`/api/products/${productId}/update-price`, {
      method: 'PUT',
      body: JSON.stringify({ price_type: priceType, new_price: newPrice, effective_date: effectiveDate }),
    });
  }

  async getPriceHistory(productId, priceType = null) {
    const url = priceType
      ? `/api/products/${productId}/price-history?price_type=${priceType}`
      : `/api/products/${productId}/price-history`;
    return this.request(url);
  }

  // Legacy wrappers
  async updateCostPrice(productId, costPrice, effectiveDate) {
    return this.updatePrice(productId, 'cost_price', costPrice, effectiveDate);
  }

  async getCostHistory(productId) {
    return this.getPriceHistory(productId, 'cost_price');
  }

  // Activity Log (admin)
  async getActivityLog(limit = 100) {
    return this.request(`/api/admin/activity-log?limit=${limit}`);
  }

  // Advanced Reports
  async getProfitLossReport(fromDate, toDate, days = 30) {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (!fromDate && !toDate) params.append('days', days);
    return this.request(`/api/reports/profit-loss?${params.toString()}`);
  }

  async getInventoryValuation() {
    return this.request('/api/reports/inventory-valuation');
  }

  async getTopProductsReport(fromDate, toDate, days = 30, limit = 25) {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (!fromDate && !toDate) params.append('days', days);
    params.append('limit', limit);
    return this.request(`/api/reports/top-products?${params.toString()}`);
  }

  async getLowStockReport() {
    return this.request('/api/reports/low-stock');
  }

  async getCustomerSalesReport(fromDate, toDate, days = 30, limit = 50) {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (!fromDate && !toDate) params.append('days', days);
    params.append('limit', limit);
    return this.request(`/api/reports/customer-sales?${params.toString()}`);
  }

  async getReceivablesAgingReport() {
    return this.request('/api/reports/receivables-aging');
  }

  // Stock Movements
  async getStockMovements(productId, limit = 100) {
    return this.request(`/api/stock-movements/${productId}?limit=${limit}`);
  }

  async getAllStockMovements(limit = 200, movementType) {
    const params = new URLSearchParams({ limit });
    if (movementType) params.append('movement_type', movementType);
    return this.request(`/api/stock-movements?${params.toString()}`);
  }

  async createManualAdjustment(productId, quantityChange, notes) {
    return this.request('/api/stock-movements/manual', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity_change: quantityChange, notes }),
    });
  }

  // Report Exports - downloads file as blob
  async downloadReportExport(endpoint, params = {}) {
    let baseUrl = API_URL || window.location.origin;
    let fullUrl = `${baseUrl}${endpoint}`;
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') searchParams.append(k, v);
    });
    const qs = searchParams.toString();
    if (qs) fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs;
    
    const token = localStorage.getItem('access_token');
    const response = await fetch(fullUrl, {
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
    });
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(err.detail || 'Export failed');
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename=(.+)/);
    const filename = match ? match[1] : 'export';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  }
}

export default new ApiClient();